/**
 * DailyCheckModal — the three-tap daily monitoring record (migration 0100).
 *
 * Appetite / toileting / demeanour as segmented button groups + an optional
 * note. Defaults to the "all normal" combination so the common case is
 * Save straight away; anything unusual is one tap per group.
 */
import { Modal } from '@/components/Modal';
import {
  type CreateDailyMonitoringInput,
  type DailyMonitoringAppetite,
  type DailyMonitoringDemeanour,
  type DailyMonitoringToileting,
  createMonitoringEntry,
} from '@/lib/api';
import { X } from 'lucide-react';
import { useState } from 'react';

export const APPETITE_OPTIONS: ReadonlyArray<{ value: DailyMonitoringAppetite; label: string }> = [
  { value: 'normal', label: 'Normal' },
  { value: 'reduced', label: 'Reduced' },
  { value: 'none', label: 'None' },
];
export const TOILETING_OPTIONS: ReadonlyArray<{ value: DailyMonitoringToileting; label: string }> =
  [
    { value: 'normal', label: 'Normal' },
    { value: 'loose', label: 'Loose' },
    { value: 'none', label: 'None' },
    { value: 'other', label: 'Other' },
  ];
export const DEMEANOUR_OPTIONS: ReadonlyArray<{ value: DailyMonitoringDemeanour; label: string }> =
  [
    { value: 'settled', label: 'Settled' },
    { value: 'anxious', label: 'Anxious' },
    { value: 'lethargic', label: 'Lethargic' },
    { value: 'other', label: 'Other' },
  ];

interface SegmentProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
}

function Segment<T extends string>({ label, options, value, onChange }: SegmentProps<T>) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </legend>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              className={`press rounded-full px-3 py-1.5 text-sm font-medium ring-1 ring-inset ${
                active
                  ? 'bg-primary text-primary-foreground ring-primary'
                  : 'bg-background text-foreground ring-border hover:bg-secondary/60'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface DailyCheckModalProps {
  bookingId: string;
  petName: string;
  onClose: () => void;
  onSaved?: () => void;
}

export function DailyCheckModal({ bookingId, petName, onClose, onSaved }: DailyCheckModalProps) {
  const [appetite, setAppetite] = useState<DailyMonitoringAppetite>('normal');
  const [toileting, setToileting] = useState<DailyMonitoringToileting>('normal');
  const [demeanour, setDemeanour] = useState<DailyMonitoringDemeanour>('settled');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: CreateDailyMonitoringInput = {
        appetite,
        toileting,
        demeanour,
        notes: notes.trim() ? notes.trim() : null,
      };
      await createMonitoringEntry(bookingId, body);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the daily check.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} aria-label="Daily check">
      <div className="w-[min(96vw,30rem)] rounded-xl bg-background p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Daily check</h3>
            <p className="mt-0.5 text-sm text-muted-foreground">{petName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <Segment
            label="Appetite"
            options={APPETITE_OPTIONS}
            value={appetite}
            onChange={setAppetite}
          />
          <Segment
            label="Toileting"
            options={TOILETING_OPTIONS}
            value={toileting}
            onChange={setToileting}
          />
          <Segment
            label="Demeanour"
            options={DEMEANOUR_OPTIONS}
            value={demeanour}
            onChange={setDemeanour}
          />
          <div>
            <label
              htmlFor="daily-check-notes"
              className="mb-1.5 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground"
            >
              Note
            </label>
            <textarea
              id="daily-check-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="Optional — anything an inspector or the owner should know"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="press rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="press rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save check'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
